// GEMS Spades (NY Ruleset) Game Engine with Blind 6+ and Ace Check support
// Based on developer specifications

export class SpadesGameEngine {
  constructor(playerName = 'You') {
    this.gameState = 'LOBBY';
    this.players = [
      { name: playerName, team: 0, hand: [], isBot: false },
      { name: 'SpadeBot', team: 1, hand: [], isBot: true },
      { name: 'CardShark_AI', team: 0, hand: [], isBot: true },
      { name: 'PokerFace_Bot', team: 1, hand: [], isBot: true }
    ];
    
    // Match settings (from pre-match modal)
    this.matchSettings = {
      allowBlindSix: true,
      allowAceCheck: true,
      wagerEmeralds: 0
    };
    
    // Team counters
    this.teamRemainingBlinds = [3, 3]; // Team 0, Team 1
    this.teamSetCount = [0, 0]; // Track how many times each team has been SET
    
    // Per-hand state
    this.currentHand = 1;
    this.dealerSeat = 3; // Player 3 is dealer (rotates each hand)
    this.leaderSeat = 0; // Player 0 (human) leads first trick
    this.spadesBroken = false;
    this.currentTrick = [];
    this.tricksPlayed = [];
    this.bids = [0, 0, 0, 0];
    this.teamBids = [0, 0];
    this.blindBids = [
      { active: false, value: 0 },
      { active: false, value: 0 },
      { active: false, value: 0 },
      { active: false, value: 0 }
    ];
    this.aceCheckDeclared = [false, false]; // Team 0, Team 1
    this.reneges = [[], []]; // Track reneges for each team
    this.booksWon = [0, 0]; // Team books won
    this.playerBooksWon = [0, 0, 0, 0]; // Individual player books won
    this.handScores = [0, 0];
    this.matchScores = [0, 0];
    this.totalBags = [0, 0]; // Track accumulated bags for each team
    this.trickWinners = []; // Track who won each trick
    this.trickCards = []; // Track cards played in each trick
    this.currentBook = 0;
    this.boston = [false, false]; // Track if team got Boston (all 13 tricks)
    this.aceCheckSuccess = [false, false]; // Track if Ace Check succeeded
    this.lastCompletedTrick = null; // Store last completed trick for display
    
    // Config
    this.config = {
      TEAM_MIN_BID: 4,
      TABLE_MIN_TOTAL_BID: 11,
      MATCH_TARGET_POINTS: 500,
      BAGS_PENALTY_THRESHOLD: 10, // Lose 100 points every 10 bags
      BAGS_PENALTY: -100,
      BOSTON_OVERRIDES_SET: true,
      BOSTON_BONUS: 200,
      ACE_CHECK_BONUS: 100,
      ACE_CHECK_PENALTY: -100,
      RENEGE_BOOK_PENALTY: 3,
      BLIND_WINDOW_DURATION: 10000 // 10 seconds
    };
    
    // Blind window state
    this.blindWindowDeadline = null;
    this.blindWindowActive = false;
  }

  // Game initialization moved to constructor

  // Create deck with NY Ruleset (52 cards + 2 Jokers, remove 2♣ and 2♥)
  createDeck() {
    const suits = ['♠', '♥', '♦', '♣'];
    const values = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'];
    const deck = [];

    // Add regular cards
    for (const suit of suits) {
      for (const value of values) {
        // Skip 2♣ and 2♥ as per NY Ruleset
        if ((suit === '♣' && value === '2') || (suit === '♥' && value === '2')) {
          continue;
        }
        deck.push({
          suit,
          value,
          color: (suit === '♥' || suit === '♦') ? 'red' : 'black',
          trumpRank: this.getTrumpRank(suit, value)
        });
      }
    }

    // Add Jokers as Spades (Big Joker and Little Joker)
    deck.push({
      suit: '♠',
      value: 'Big Joker',
      color: 'black',
      isJoker: true,
      jokerRank: 15, // Higher than Ace
      trumpRank: 1000
    });
    
    deck.push({
      suit: '♠', 
      value: 'Little Joker',
      color: 'black',
      isJoker: true,
      jokerRank: 14.5, // Between Ace and Big Joker
      trumpRank: 999
    });

    return this.shuffleDeck(deck);
  }

  // Trump hierarchy from spec: Big Joker > Little Joker > 2♦ > 2♠ > A♠ > K♠...
  getTrumpRank(suit, value) {
    // Handle Jokers (now as Spades)
    if (suit === '♠' && value === 'Big Joker') return 1000;
    if (suit === '♠' && value === 'Little Joker') return 999;
    
    if (suit === '♦' && value === '2') return 998;
    if (suit === '♠' && value === '2') return 997;
    
    if (suit === '♠') {
      const spadeRanks = { 'A': 996, 'K': 995, 'Q': 994, 'J': 993, '10': 992, '9': 991, '8': 990, '7': 989, '6': 988, '5': 987, '4': 986, '3': 985 };
      return spadeRanks[value] || 0;
    }
    
    return 0; // Non-trump cards
  }

  shuffleDeck(deck) {
    const shuffled = [...deck];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // Deal 13 cards to each player
  dealCards() {
    const deck = this.createDeck();
    this.players.forEach(player => player.hand = []);
    
    for (let i = 0; i < 13; i++) {
      for (let playerIndex = 0; playerIndex < 4; playerIndex++) {
        const cardIndex = i * 4 + playerIndex;
        if (cardIndex < deck.length) {
          this.players[playerIndex].hand.push(deck[cardIndex]);
        }
      }
    }
    
    // Sort each player's hand by suit for better organization
    // Order: Spades (including 2♦ as trump), Diamonds, Clubs, Hearts
    this.players.forEach(player => {
      player.hand.sort((a, b) => {
        // Check if card is a trump (spade or 2♦)
        const isTrump = (card) => {
          return card.suit === '♠' || (card.suit === '♦' && card.value === '2') || card.isJoker;
        };
        
        // Primary: Trumps first (Spades + 2♦)
        const aIsTrump = isTrump(a);
        const bIsTrump = isTrump(b);
        
        if (aIsTrump && !bIsTrump) return -1; // a is trump, b is not -> a comes first
        if (!aIsTrump && bIsTrump) return 1;  // b is trump, a is not -> b comes first
        
        // Both are trumps - sort by trump rank (highest first)
        if (aIsTrump && bIsTrump) {
          return (b.trumpRank || 0) - (a.trumpRank || 0);
        }
        
        // Neither is trump - sort by suit order: Diamonds, Clubs, Hearts
        const suitOrder = { '♦': 0, '♣': 1, '♥': 2 };
        if (suitOrder[a.suit] !== suitOrder[b.suit]) {
          return suitOrder[a.suit] - suitOrder[b.suit];
        }
        
        // Same suit - sort by value (high to low)
        const valueOrder = { 'A': 14, 'K': 13, 'Q': 12, 'J': 11, '10': 10, '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2 };
        return valueOrder[b.value] - valueOrder[a.value];
      });
    });
  }

  // Start new hand with blind window phase
  startNewHand() {
    // Rotate dealer and increment hand (except for first hand)
    if (this.currentHand > 1 || this.gameState === 'HAND_SCORING') {
      this.dealerSeat = (this.dealerSeat + 1) % 4;
      this.leaderSeat = (this.dealerSeat + 1) % 4;
      this.currentHand++;
    }
    
    this.gameState = 'HAND_INIT';
    this.spadesBroken = false;
    this.currentTrick = [];
    this.lastCompletedTrick = null; // Reset last completed trick
    this.tricksPlayed = [];
    this.trickWinners = []; // Reset trick winners for new hand
    this.trickCards = []; // Reset trick cards for new hand
    this.currentBook = 0;
    this.bids = [0, 0, 0, 0];
    this.blindBids = [
      { active: false, value: 0 },
      { active: false, value: 0 },
      { active: false, value: 0 },
      { active: false, value: 0 }
    ];
    this.booksWon = [0, 0];
    this.playerBooksWon = [0, 0, 0, 0]; // Reset individual player books
    this.handScores = [0, 0]; // Ensure hand scores are reset
    this.aceCheckDeclared = [false, false];
    this.boston = [false, false]; // Reset Boston flags
    this.aceCheckSuccess = [false, false]; // Reset Ace Check success flags
    this.reneges = [[], []]; // Reset reneges for new hand
    
    // Start dealing cards to players
    this.gameState = 'DEALING';
    this.dealCards();
    
    // After dealing is complete, allow players to see their cards
    // The frontend will transition to bidding after showing the cards
  }
  
  // Method to transition from dealing to bidding (called by frontend)
  startBiddingPhase() {
    this.gameState = 'BIDDING';
  }

  // Check if blind bids are allowed (only when team is losing by 100+ points)
  canTeamUseBlinds(team) {
    if (!this.matchSettings.allowBlindSix) return false;
    if (this.teamRemainingBlinds[team] <= 0) return false;
    
    // Only allow blind bids when team is losing by 100+ points
    const otherTeam = team === 0 ? 1 : 0;
    const scoreDifference = this.matchScores[otherTeam] - this.matchScores[team];
    return scoreDifference >= 100;
  }

  // Start bidding phase with proper order
  startBiddingPhase() {
    // Hand 1 special rule: "Bid Itself" - no bidding, skip directly to playing
    if (this.currentHand === 1) {
      // Skip bidding entirely and go straight to playing
      this.bids = [0, 0, 0, 0];
      this.teamBids = [0, 0];
      this.gameState = 'PLAYING'; // Go directly to PLAYING, skip BIDDING state
      console.log('Hand 1: Bid Itself - skipping bidding phase');
      return;
    }
    
    // For all other hands, normal bidding
    this.closeBlindWindow(); // Ensure blind window is closed
    this.gameState = 'BIDDING';
  }
  
  // Get current bidder based on who plays first
  // If Team 1 (seats 0,2) plays first, Team 2 (seats 1,3) bids first
  // If Team 2 (seats 1,3) plays first, Team 1 (seats 0,2) bids first
  // Within a team: partner bids before user (seat 2 before 0, seat 3 before 1)
  getCurrentBidder() {
    if (this.gameState !== 'BIDDING') return -1;
    
    // Determine bidding order based on who leads first trick
    const leaderTeam = this.players[this.leaderSeat].team;
    
    // If Team 0 leads, Team 1 bids first (seats 3, 1)
    // If Team 1 leads, Team 0 bids first (seats 2, 0)
    let biddingOrder;
    if (leaderTeam === 0) {
      // Team 0 leads, so Team 1 bids first: 3, 1, then 2, 0
      biddingOrder = [3, 1, 2, 0];
    } else {
      // Team 1 leads, so Team 0 bids first: 2, 0, then 3, 1
      biddingOrder = [2, 0, 3, 1];
    }
    
    // Find first player in bidding order who hasn't bid yet
    for (let seat of biddingOrder) {
      if (this.bids[seat] === 0 && !this.blindBids[seat].active) {
        return seat;
      }
    }
    
    return -1; // All players have bid
  }

  // Start 10-second blind window (only if conditions are met)
  // Note: The frontend handles the timer and calls closeBlindWindow() when time expires
  startBlindWindow() {
    // Check if any team can actually use blind bids
    const team0CanBlind = this.canTeamUseBlinds(0);
    const team1CanBlind = this.canTeamUseBlinds(1);
    
    if (!team0CanBlind && !team1CanBlind) {
      // No team can use blind bids, skip blind window
      this.gameState = 'BIDDING';
      return false;
    }
    
    this.gameState = 'BLIND_WINDOW';
    this.blindWindowActive = true;
    this.blindWindowDeadline = Date.now() + this.config.BLIND_WINDOW_DURATION;
    
    // Frontend handles the timer via useEffect - no setTimeout here to avoid race conditions
    return true;
  }

  // Declare blind bid during blind window
  declareBlind(playerSeat, blindValue) {
    if (this.gameState !== 'BLIND_WINDOW') return false;
    if (blindValue < 6 || blindValue > 13) return false;
    
    const playerTeam = this.players[playerSeat].team;
    
    // Check if team has remaining blinds
    if (this.teamRemainingBlinds[playerTeam] <= 0) return false;
    
    // Declare blind
    this.blindBids[playerSeat] = { active: true, value: blindValue };
    this.bids[playerSeat] = blindValue; // Lock the bid
    this.teamRemainingBlinds[playerTeam]--;
    
    return true;
  }

  // Decline blind during blind window
  declineBlind(playerSeat) {
    if (this.gameState !== 'BLIND_WINDOW') return false;
    
    this.blindBids[playerSeat] = { active: false, value: 0 };
    return true;
  }

  // Close blind window and transition to bidding (cards already dealt)
  closeBlindWindow() {
    if (this.gameState !== 'BLIND_WINDOW') return;
    
    this.blindWindowActive = false;
    this.blindWindowDeadline = null;
    
    // Cards are already dealt - just transition to bidding
    this.gameState = 'BIDDING';
  }

  // Get remaining time in blind window
  getBlindWindowTimeRemaining() {
    if (!this.blindWindowActive || !this.blindWindowDeadline) return 0;
    return Math.max(0, this.blindWindowDeadline - Date.now());
  }

  // Place bid with validation (updated for blind players)
  placeBid(playerSeat, bidAmount, isBlind = false) {
    if (this.gameState !== 'BIDDING') return false;
    
    // Blind players already have their bids locked
    if (this.blindBids[playerSeat].active) {
      // Bid is already set during blind window
      return true;
    }
    
    if (bidAmount < 1) return false; // Minimum bid is 1
    
    this.bids[playerSeat] = bidAmount;

    // Calculate team bids
    this.teamBids[0] = this.bids[0] + this.bids[2]; // Team 0: seats 0 & 2
    this.teamBids[1] = this.bids[1] + this.bids[3]; // Team 1: seats 1 & 3

    // Check if all players have bid
    if (this.bids.every(bid => bid > 0)) {
      const validation = this.validateBids();
      return validation.valid;
    }
    
    return true;
  }

  // Declare Ace Check during bidding phase
  declareAceCheck(team) {
    if (this.gameState !== 'BIDDING') return false;
    if (!this.matchSettings.allowAceCheck) return false;
    
    this.aceCheckDeclared[team] = true;
    return true;
  }

  // Cancel Ace Check during bidding phase
  cancelAceCheck(team) {
    if (this.gameState !== 'BIDDING') return false;
    
    this.aceCheckDeclared[team] = false;
    return true;
  }

  // Validate all bids meet minimum requirements
  validateBids() {
    // Calculate team totals
    this.teamBids[0] = this.bids[0] + this.bids[2];
    this.teamBids[1] = this.bids[1] + this.bids[3];
    
    const totalBid = this.teamBids[0] + this.teamBids[1];
    
    // Check if minimum requirements are met
    const team0MeetsMin = this.teamBids[0] >= this.config.TEAM_MIN_BID;
    const team1MeetsMin = this.teamBids[1] >= this.config.TEAM_MIN_BID;
    const tableMeetsMin = totalBid >= this.config.TABLE_MIN_TOTAL_BID;
    
    if (!team0MeetsMin || !team1MeetsMin || !tableMeetsMin) {
      // MISDEAL - force re-bidding
      console.log('MISDEAL! Bids do not meet minimum requirements. Re-bidding required.');
      console.log(`Team 0: ${this.teamBids[0]} (min: ${this.config.TEAM_MIN_BID})`);
      console.log(`Team 1: ${this.teamBids[1]} (min: ${this.config.TEAM_MIN_BID})`);
      console.log(`Table: ${totalBid} (min: ${this.config.TABLE_MIN_TOTAL_BID})`);
      
      // Reset all bids and restart bidding
      this.bids = [0, 0, 0, 0];
      this.blindBids = [
        { active: false, value: 0 },
        { active: false, value: 0 },
        { active: false, value: 0 },
        { active: false, value: 0 }
      ];
      this.teamBids = [0, 0];
      this.gameState = 'BIDDING';
      
      return {
        valid: false,
        reason: `Misdeal! Bids must meet minimums: Each team ≥ ${this.config.TEAM_MIN_BID}, Table total ≥ ${this.config.TABLE_MIN_TOTAL_BID}. Please bid again.`
      };
    }
    
    // All bids valid, transition to playing
    this.gameState = 'PLAYING';
    this.leaderSeat = (this.dealerSeat + 1) % 4; // Player after dealer leads first trick
    this.currentTrick = []; // Ensure trick is empty to start
    
    console.log('Bidding complete! First trick leader:', this.leaderSeat, 'Player name:', this.players[this.leaderSeat].name);
    
    return { valid: true };
  }

  // Play a card with proper turn validation (clockwise: 0->3->2->1->0 from player view)
  playCard(playerSeat, cardIndex) {
    if (this.gameState !== 'PLAYING') return false;
    
    // If starting a new trick, clear the last completed trick
    if (this.currentTrick.length === 0) {
      this.lastCompletedTrick = null;
    }
    
    // Validate turn order - clockwise from player's view: 0->3->2->1->0
    // This creates: bottom -> left -> top -> right -> bottom progression
    const getNextSeat = (seat, steps) => {
      // Go backwards through seat numbers (counter-clockwise in seat IDs = clockwise visually)
      return (seat - steps + 4) % 4;
    };
    
    const expectedPlayer = this.currentTrick.length === 0 ? 
      this.leaderSeat : 
      getNextSeat(this.leaderSeat, this.currentTrick.length);
    
    if (playerSeat !== expectedPlayer) return false; // Not this player's turn
    
    const player = this.players[playerSeat];
    if (!player || cardIndex < 0 || cardIndex >= player.hand.length) return false;
    
    const card = player.hand[cardIndex];
    
    // Validate play (follow suit, etc.)
    if (!this.isValidPlay(playerSeat, card)) {
      return false;
    }
    
    // Remove card from hand and add to trick
    player.hand.splice(cardIndex, 1);
    this.currentTrick.push({ card, playerSeat });
    
    // Check if spades broken
    if (!this.spadesBroken && this.isSpadeOrTrump(card)) {
      if (this.currentTrick.length > 1) { // Trumping a non-spade lead
        this.spadesBroken = true;
      }
    }
    
    // If trick complete (4 cards), resolve it
    if (this.currentTrick.length === 4) {
      this.resolveTrick();
    }
    
    return true;
  }

  isValidPlay(playerSeat, card) {
    const player = this.players[playerSeat];
    
    // If leading the trick
    if (this.currentTrick.length === 0) {
      // Can't lead spades/trump until broken, unless only spades in hand
      if (this.isSpadeOrTrump(card) && !this.spadesBroken) {
        const onlySpades = player.hand.every(c => this.isSpadeOrTrump(c));
        return onlySpades;
      }
      return true;
    }
    
    // RENEGE DETECTION DISABLED - Just prevent invalid plays without penalties
    // Following suit - must follow if able, but no renege recording or penalties
    const leadCard = this.currentTrick[0].card;
    const leadSuit = leadCard.suit;
    const leadIsTrump = this.isSpadeOrTrump(leadCard);
    
    if (leadIsTrump) {
      // Trump was led - must follow with trump if you have it
      const hasTrump = player.hand.some(c => this.isSpadeOrTrump(c));
      const playingTrump = this.isSpadeOrTrump(card);
      
      // Block invalid play but don't record renege
      if (hasTrump && !playingTrump) {
        return false;
      }
    } else {
      // Non-trump was led - must follow suit if you have it (2♦ doesn't count as a diamond)
      const hasLedSuit = player.hand.some(c => 
        c.suit === leadSuit && !(c.suit === '♦' && c.value === '2')
      );
      
      const playingLedSuit = card.suit === leadSuit && !(card.suit === '♦' && card.value === '2');
      
      // Block invalid play but don't record renege
      if (hasLedSuit && !playingLedSuit) {
        return false;
      }
    }
    
    return true;
  }

  isSpadeOrTrump(card) {
    return card.suit === '♠' || 
           (card.suit === '♦' && card.value === '2');
  }

  resolveTrick() {
    // Store the completed trick for display before clearing
    this.lastCompletedTrick = [...this.currentTrick];
    
    // Determine winner by trump hierarchy
    let winnerIndex = 0;
    let highestRank = -1;
    
    // Determine the lead suit/trump status
    const leadCard = this.currentTrick[0].card;
    const leadIsTrump = this.isSpadeOrTrump(leadCard);
    const leadSuit = leadCard.suit;
    
    this.currentTrick.forEach((play, index) => {
      const { card } = play;
      let rank = card.trumpRank || 0;
      
      // If not trump, check if it's highest of led suit
      if (rank === 0) {
        // Only non-trump cards can win by following suit
        if (!leadIsTrump && card.suit === leadSuit && !(card.suit === '♦' && card.value === '2')) {
          // Non-trump card following non-trump lead
          const valueRanks = { 'A': 14, 'K': 13, 'Q': 12, 'J': 11, '10': 10, '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2 };
          rank = valueRanks[card.value] || 0;
        }
        // Otherwise rank stays 0 (can't win)
      }
      
      if (rank > highestRank) {
        highestRank = rank;
        winnerIndex = index;
      }
    });
    
    const winnerSeat = this.currentTrick[winnerIndex].playerSeat;
    const winnerTeam = this.players[winnerSeat].team;
    
    this.booksWon[winnerTeam]++;
    this.playerBooksWon[winnerSeat]++; // Track individual player books
    this.tricksPlayed.push([...this.currentTrick]);
    this.currentTrick = [];
    this.currentBook++;
    this.leaderSeat = winnerSeat;
    
    // Check if hand is complete
    if (this.currentBook === 13) {
      this.scoreHand();
    }
  }

  recordRenege(playerSeat, card) {
    const team = this.players[playerSeat].team;
    this.reneges[team].push({
      seat: playerSeat,
      bookIndex: this.currentBook,
      offendingCard: card
    });
  }

  scoreHand() {
    this.gameState = 'HAND_SCORING';
    
    // Special handling for Hand 1: "Bid Itself" - score = 10 × books won (no bidding)
    if (this.currentHand === 1) {
      // Calculate team books from individual player books
      const actualBooksWon = [
        this.playerBooksWon[0] + this.playerBooksWon[2], // Team 0: seats 0 and 2
        this.playerBooksWon[1] + this.playerBooksWon[3]  // Team 1: seats 1 and 3
      ];
      
      // Hand 1: Score = 10 points per book won (no bids, no bags, no penalties)
      this.handScores[0] = actualBooksWon[0] * 10;
      this.handScores[1] = actualBooksWon[1] * 10;
      this.matchScores[0] += this.handScores[0];
      this.matchScores[1] += this.handScores[1];
      
      console.log(`Hand 1 "Bid Itself": Team 1 won ${actualBooksWon[0]} books = ${this.handScores[0]} points`);
      console.log(`Hand 1 "Bid Itself": Team 2 won ${actualBooksWon[1]} books = ${this.handScores[1]} points`);
      
      // Don't set bids for Hand 1 - there are no bids
      this.bids = [0, 0, 0, 0];
      this.teamBids = [0, 0];
      
      // Check for match end
      if (this.matchScores[0] >= this.config.MATCH_TARGET_POINTS || 
          this.matchScores[1] >= this.config.MATCH_TARGET_POINTS) {
        this.gameState = 'MATCH_COMPLETE';
      }
      
      return; // Exit early for Hand 1
    }
    
    // For all other hands (Hand 2+), normal scoring with bids
    // Calculate team books from individual player books (most accurate)
    const actualBooksWon = [
      this.playerBooksWon[0] + this.playerBooksWon[2], // Team 0: seats 0 and 2
      this.playerBooksWon[1] + this.playerBooksWon[3]  // Team 1: seats 1 and 3
    ];
    
    // Calculate effective books after renege penalties
    const effectiveBooksWon = [...actualBooksWon];
    
    // RENEGE PENALTIES DISABLED - No penalties applied for now
    // Will revisit renege detection implementation later
    
    // Calculate overs (bags for this hand) using effective books
    const overs = [
      Math.max(0, effectiveBooksWon[0] - this.teamBids[0]),
      Math.max(0, effectiveBooksWon[1] - this.teamBids[1])
    ];
    
    console.log('Score Calculation:', {
      team0: { bid: this.teamBids[0], books: actualBooksWon[0], effective: effectiveBooksWon[0], overs: overs[0] },
      team1: { bid: this.teamBids[1], books: actualBooksWon[1], effective: effectiveBooksWon[1], overs: overs[1] }
    });
    
    // Check for Boston
    this.boston[0] = effectiveBooksWon[0] === 13;
    this.boston[1] = effectiveBooksWon[1] === 13;
    
    // Score each team
    for (let team = 0; team < 2; team++) {
      let teamScore = 0;
      
      console.log(`\n=== SCORING TEAM ${team} (displayed as Team ${team + 1}) ===`);
      console.log(`Team Bid: ${this.teamBids[team]}`);
      console.log(`Actual Books Won: ${actualBooksWon[team]}`);
      console.log(`Effective Books Won: ${effectiveBooksWon[team]}`);
      console.log(`Overs (Bags): ${overs[team]}`);
      console.log(`Made Bid? ${effectiveBooksWon[team] >= this.teamBids[team]}`);
      
      // Check if team made their bid
      if (effectiveBooksWon[team] >= this.teamBids[team]) {
        console.log(`SET check: overs >= 3? ${overs[team]} >= 3 = ${overs[team] >= 3}`);
        
        // Check for SET (TEAM gets 3 or more bags in one hand)
        if (overs[team] >= 3) {
          // SET PENALTY: Lose 10 points per bid
          teamScore = -10 * this.teamBids[team];
          console.log(`BEFORE INCREMENT: teamSetCount[${team}] = ${this.teamSetCount[team]}`);
          this.teamSetCount[team]++; // Increment set counter
          console.log(`AFTER INCREMENT: teamSetCount[${team}] = ${this.teamSetCount[team]}`);
          console.log(`✗ TEAM SET (3+ BAGS)! Score = ${teamScore} points. SET count now: ${this.teamSetCount[team]}`);
        } else {
          // Made bid - get points for bid plus overs (bags)
          teamScore = 10 * this.teamBids[team] + overs[team];
          console.log(`✓ MADE BID! Score = ${teamScore} points (${10 * this.teamBids[team]} + ${overs[team]} bags)`);
          
          // Track total bags (only if not set)
          this.totalBags[team] += overs[team];
        }
      } else {
        // Failed bid - lose points AND increment SET counter
        teamScore = -10 * this.teamBids[team];
        console.log(`BEFORE INCREMENT: teamSetCount[${team}] = ${this.teamSetCount[team]}`);
        this.teamSetCount[team]++; // Increment set counter for failed bid
        console.log(`AFTER INCREMENT: teamSetCount[${team}] = ${this.teamSetCount[team]}`);
        console.log(`✗ TEAM SET (FAILED BID)! Bid ${this.teamBids[team]}, won ${effectiveBooksWon[team]} = ${teamScore} points. SET count now: ${this.teamSetCount[team]}`);
      }
      
      console.log(`Team ${team + 1} FINAL SCORE before modifiers: ${teamScore}`);
      
      // Apply blind bid modifiers
      [0, 2].forEach(seat => { // Team 0 seats
        if (team === 0 && this.blindBids[seat].active) {
          const blindPoints = effectiveBooksWon[team] >= this.teamBids[team] ? 
            10 * this.bids[seat] : -10 * this.bids[seat];
          teamScore += blindPoints; // Double the individual's contribution
        }
      });
      
      [1, 3].forEach(seat => { // Team 1 seats  
        if (team === 1 && this.blindBids[seat].active) {
          const blindPoints = effectiveBooksWon[team] >= this.teamBids[team] ? 
            10 * this.bids[seat] : -10 * this.bids[seat];
          teamScore += blindPoints; // Double the individual's contribution
        }
      });
      
      // Boston bonus
      if (this.boston[team]) {
        teamScore += this.config.BOSTON_BONUS;
      }
      
      // Ace Check bonus/penalty
      if (this.aceCheckDeclared[team]) {
        teamScore += this.aceCheckSuccess[team] ? 
          this.config.ACE_CHECK_BONUS : this.config.ACE_CHECK_PENALTY;
      }
      
      // Check for bags penalty (every 10 bags accumulated)
      // Only check if we actually added bags this hand
      if (overs[team] > 0 && overs[team] < 3) {
        // We added bags (and weren't set)
        if (this.totalBags[team] >= this.config.BAGS_PENALTY_THRESHOLD) {
          const penaltyCount = Math.floor(this.totalBags[team] / this.config.BAGS_PENALTY_THRESHOLD);
          const penaltyPoints = penaltyCount * this.config.BAGS_PENALTY;
          console.log(`Team ${team + 1} BAGS PENALTY! Total bags: ${this.totalBags[team]}, applying ${penaltyCount} x ${this.config.BAGS_PENALTY} = ${penaltyPoints}`);
          teamScore += penaltyPoints;
          // Reset bags counter after penalty
          this.totalBags[team] = this.totalBags[team] % this.config.BAGS_PENALTY_THRESHOLD;
        }
      }
      
      this.handScores[team] = teamScore;
      this.matchScores[team] += teamScore;
      
      console.log(`Team ${team + 1} FINAL HAND SCORE: ${this.handScores[team]}, MATCH TOTAL: ${this.matchScores[team]}`);
    }
    
    // Check for match end conditions
    if (this.matchScores[0] >= this.config.MATCH_TARGET_POINTS || 
        this.matchScores[1] >= this.config.MATCH_TARGET_POINTS) {
      this.gameState = 'MATCH_COMPLETE';
    } else if (this.teamSetCount[0] >= 3) {
      // Team 0 set 3 times - Team 1 wins
      this.gameState = 'MATCH_COMPLETE';
      console.log('Team 1 SET 3 times! Team 2 wins automatically!');
    } else if (this.teamSetCount[1] >= 3) {
      // Team 1 set 3 times - Team 0 wins
      this.gameState = 'MATCH_COMPLETE';
      console.log('Team 2 SET 3 times! Team 1 wins automatically!');
    }
    
    // Note: Don't increment currentHand here - it will be incremented when starting next hand
  }

  // Get current game state for UI
  getGameState() {
    return {
      gameState: this.gameState,
      currentHand: this.currentHand,
      dealerSeat: this.dealerSeat,
      leaderSeat: this.leaderSeat,
      players: this.players.map(p => ({
        ...p,
        hand: p.isBot ? Array(p.hand.length).fill({ suit: 'BACK', value: '?', color: 'blue' }) : p.hand
      })),
      spadesBroken: this.spadesBroken,
      currentTrick: this.currentTrick,
      lastCompletedTrick: this.lastCompletedTrick,
      bids: this.bids,
      teamBids: this.teamBids,
      blindBids: this.blindBids,
      aceCheckDeclared: this.aceCheckDeclared,
      teamRemainingBlinds: this.teamRemainingBlinds,
      matchSettings: this.matchSettings,
      booksWon: this.booksWon,
      playerBooksWon: this.playerBooksWon,
      handScores: this.handScores,
      matchScores: this.matchScores,
      totalBags: this.totalBags,
      teamSetCount: this.teamSetCount,
      reneges: this.reneges,
      currentBook: this.currentBook,
      blindWindowActive: this.blindWindowActive,
      blindWindowTimeRemaining: this.getBlindWindowTimeRemaining(),
      config: this.config
    };
  }
}